// @ts-strict-ignore
import Descriptions from 'antd/lib/descriptions';
import Popconfirm from 'antd/lib/popconfirm';
import Space from 'antd/lib/space';
import * as React from 'react';
import { AppComponentProps, AppRouteComponentMatchParams, OrgSettingsTabType } from '../../typescriptHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { getIsApplePlatform, getReleaseTypeFromReleaseTypeForNonBlockingSteps } from '../../helpers';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteAppRequest, TRY_DELETE_APP_ENTITY, tryDeleteApp } from '../../actions/app';
import { RootState } from '../../reducers/reducers';
import { createAppSettingsRouteWithSettingType, createOrgSettingsRoute } from '../../helpers/routeHelpers';
import {
    selectApp,
    selectAppGroupIdForApp,
    selectAppSettings,
    selectAppStoreType,
    selectIsWorkflowsCIStepEnabled,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectReleaseStepsByReleaseType,
    selectShouldHideNonBlockingSteps,
    selectUserIsRunwayAdmin,
    selectBundleSizeDeviceModelId,
    selectASCDeviceModels,
} from '../../selectors/selectors';
import Text from 'antd/lib/typography/Text';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import Button from 'antd/lib/button';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Typography from 'antd/lib/typography';
import { DeleteOutlined, EditOutlined, FilterOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from '../styles/settingsStyles';
import {
    displayTextForAppPlatform,
    displayTextForAssumePointReleasesAreHotfixes,
    displayTextForReleaseStepType,
    displayTextForReleaseType,
} from '../../helpers/displayHelpers';
import { getMdmPlatformOverride } from '../../helpers/mdmPlatformOverride';
import { selectAvailableDestinations } from '../../selectors/summaryViewSelectors';
import EditAppSettingsModal from './EditAppSettingsModal';
import Tooltip from 'antd/lib/tooltip';
import BumpVersionFilesModal from './BumpVersionFilesModal';
import NonBlockingReleaseStepsModal from './NonBlockingReleaseStepsModal';
import FilterPathsModal from './FilterPathsModal';
import { globalMessage } from '../../utils/globalMessage';
import {
    UserFacingExternalVersionTypes,
    ReleaseStepBlockingConfig,
    ReleaseTypesForNonBlockingSteps,
    SettingType,
} from '../../types';
import { BUMP_VERSION_DOCS_URL } from '../../constants';
import Link from 'antd/lib/typography/Link';
import List from 'antd/lib/list';
import Divider from 'antd/lib/divider';
import TranslationDirectoriesModal from './TranslationDirectoriesModal';
import EditBundleSizeDeviceModelModal from './EditBundleSizeDeviceModelModal';
import { useError, useIsLoading, useWillUpdate } from '@/utils/hooks/helpers';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

const AppSettingDescription: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { appIdParam, orgIdParam } = useParams<AppRouteComponentMatchParams>();
    const dispatch = useDispatch();
    const history = useHistory();

    const [showAllBumpFiles, setShowAllBumpFiles] = React.useState(false);
    const [showAllTranslationDirs, setShowAllTranslationDirs] = React.useState(false);

    const appId = useSelector((state: RootState) => selectApp(state, props.appId).appId);
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const hasMultiplePlatforms = useSelector(
        (state: RootState) =>
            new Set(selectAvailableDestinations(state, props.orgId, props.appId).map((d) => d.platform)).size > 1,
    );
    const appGroupId = useSelector((state: RootState) => selectAppGroupIdForApp(state, props.appId));
    const releaseStepsByReleaseType = useSelector((state: RootState) =>
        selectReleaseStepsByReleaseType(state, props.appId),
    );
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );
    const nonBlockingStepsConfig = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, props.appId),
    );

    const bundleSizeDeviceModelId = useSelector(selectBundleSizeDeviceModelId(props.appId));
    const ascDeviceModels = useSelector(selectASCDeviceModels(props.appId));
    const bundleSizeDeviceModelDisplay =
        bundleSizeDeviceModelId ?
            (ascDeviceModels.find((m) => m.id === bundleSizeDeviceModelId)?.displayName ?? 'Invalid')
        :   'Universal';

    const [showEditAppSettings, setShowEditAppSettings] = React.useState(false);
    const [showBumpVersionFilesModal, setShowBumpVersionFilesModal] = React.useState(false);
    const [showTranslationDirectoriesModal, setShowTranslationDirectoriesModal] = React.useState(false);
    const [showNonBlockingStepsModal, setShowNonBlockingStepsModal] = React.useState(false);
    const [showFilterPathsModal, setShowFilterPathsModal] = React.useState(false);
    const [showEditBundleSizeDeviceModelModal, setShowEditBundleSizeDeviceModelModal] = React.useState(false);

    const integrationsRoute = createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.Integrations);

    const deleteAppIsLoading = useIsLoading(TRY_DELETE_APP_ENTITY, props.appId);
    const deleteAppError = useError(TRY_DELETE_APP_ENTITY, props.appId);
    const willDeleteApp = useWillUpdate(TRY_DELETE_APP_ENTITY, props.appId);

    const hasIntegrations = appSettings.integrations.length > 0;

    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, props.appId),
    );

    const deleteApp = () => {
        const request: DeleteAppRequest = {
            orgId: props.orgId,
            appId: props.appId,
            appGroupId,
            orgIdParam,
            appIdParam,
        };

        dispatch(tryDeleteApp(request));
    };

    type ReleaseTypeItem = {
        key: ReleaseTypesForNonBlockingSteps;
        label: string;
    };

    const items: ReleaseTypeItem[] = [
        {
            key: ReleaseTypesForNonBlockingSteps.Standard,
            label: 'Standard',
        },
        {
            key: ReleaseTypesForNonBlockingSteps.Hotfix,
            label: 'Hotfixes',
        },
    ];

    const isUserRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    if (isUserRunwayAdmin) {
        items.push({
            key: ReleaseTypesForNonBlockingSteps.Rollback,
            label: 'Rollbacks',
        });
    }

    React.useEffect(() => {
        if (deleteAppError) {
            globalMessage.error(deleteAppError);
        }
    }, [deleteAppError]);

    React.useEffect(() => {
        if (willDeleteApp) {
            globalMessage.success('App deleted!');

            const orgRoute = createOrgSettingsRoute(orgIdParam, OrgSettingsTabType.Team);

            history.replace(orgRoute);
        }
    }, [willDeleteApp]);

    const bumpFiles = showAllBumpFiles ? appSettings.bumpVersionFiles : appSettings.bumpVersionFiles.slice(0, 3);
    const truncatedDirsCount = 3;
    const translationDirs =
        showAllTranslationDirs ?
            appSettings.translationDirectories
        :   appSettings.translationDirectories.slice(0, truncatedDirsCount);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Space>
                    <Text style={styles.appSettingsSectionTitleStyle}>General</Text>
                </Space>
                <Popconfirm
                    title="Are you sure you want to delete this app?"
                    onConfirm={deleteApp}
                    okButtonProps={{ danger: true }}
                    okText="Yes, delete"
                    placement="topRight"
                >
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                        danger
                        loading={deleteAppIsLoading}
                        type="link"
                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                    >
                        Delete app
                    </PermissionedActionButton>
                </Popconfirm>
            </div>
            <Descriptions
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                style={{ marginBottom: 24 }}
                labelStyle={{ width: hasMultiplePlatforms ? 320 : 300 }}
            >
                <Descriptions.Item label="App name">{appSettings.appName}</Descriptions.Item>
                <Descriptions.Item label="App identifier">{appId}</Descriptions.Item>
                {!hasMultiplePlatforms && (
                    <Descriptions.Item label="Platform">
                        {getMdmPlatformOverride(appSettings.shouldDisplayAsMdm)?.label ??
                            displayTextForAppPlatform(appSettings.platform, appSettings.appStoreType)}
                    </Descriptions.Item>
                )}
                <Descriptions.Item
                    label={
                        <Space>
                            <Text style={{ color: runwayColors.darkTextGrey }}>Version settings</Text>
                            <TooltipInfoIcon tooltipContent="The settings used by Runway to understand how your team does versioning, e.g. to predict upcoming versions and correctly render custom version formats your team may use in different contexts." />
                        </Space>
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div>
                                <Text>Default release type: </Text>
                                {displayTextForReleaseType(appSettings.defaultReleaseType)}
                            </div>
                            <div>
                                <Text>Assume point releases are hotfixes: </Text>
                                {displayTextForAssumePointReleasesAreHotfixes(
                                    appSettings.assumePointReleasesAreHotfixes,
                                )}
                            </div>
                            {(() => {
                                const enabledCount = UserFacingExternalVersionTypes.filter(
                                    (type) => appSettings.externalVersioningConfigForType?.[type]?.pattern,
                                ).length;
                                if (enabledCount === 0) return null;
                                return (
                                    <div>
                                        <Text>Custom versioning: </Text>
                                        <Text>{enabledCount === 1 ? '1 enabled' : `${enabledCount} enabled`}</Text>
                                    </div>
                                );
                            })()}
                        </div>
                        <div>
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => setShowEditAppSettings(true)}
                            >
                                Edit
                            </PermissionedActionButton>
                        </div>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item
                    label={
                        <Space>
                            <Text style={{ color: runwayColors.darkTextGrey }}>Version files in codebase</Text>
                            <TooltipInfoIcon
                                tooltipContent={
                                    <>
                                        File path(s) corresponding to your app&apos;s version files in your codebase.
                                        Runway will bump the version in these files during manual or automated version
                                        bumps.
                                        <br />
                                        <br />
                                        Learn more about how we detect versions in files in our&nbsp;
                                        <Link
                                            style={{ color: runwayColors.white, textDecoration: 'underline' }}
                                            href={BUMP_VERSION_DOCS_URL}
                                            target="_blank"
                                        >
                                            documentation
                                        </Link>
                                        .
                                    </>
                                }
                            />
                        </Space>
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {bumpFiles.length === 0 ?
                                <>
                                    <Text disabled style={{ cursor: 'initial' }}>
                                        No files with the expected version format found
                                    </Text>
                                </>
                            :   <List
                                    size="small"
                                    split={false}
                                    loadMore={
                                        appSettings.bumpVersionFiles.length > 3 && !showAllBumpFiles ?
                                            <Button
                                                type="link"
                                                onClick={() => setShowAllBumpFiles(true)}
                                                style={{ padding: 0 }}
                                            >
                                                Show more
                                            </Button>
                                        :   null
                                    }
                                >
                                    {bumpFiles.map((file, i) => (
                                        <List.Item
                                            key={i}
                                            style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}
                                        >
                                            {file.fileName}
                                            {file.customPattern && (
                                                <Tooltip
                                                    title={
                                                        <Text code style={{ color: runwayColors.white }}>
                                                            {file.customPattern}
                                                        </Text>
                                                    }
                                                >
                                                    <FilterOutlined
                                                        style={{ marginLeft: 8, color: runwayColors.statusGrey }}
                                                    />
                                                </Tooltip>
                                            )}
                                            {!file.currentVersion && (
                                                <Text type="danger" style={{ marginLeft: 8 }}>
                                                    {file.errMsg ??
                                                        `Unable to detect current version on branch: '${file.branch}'`}
                                                    {file.errDetail && (
                                                        <>
                                                            &nbsp;&nbsp;
                                                            <Tooltip title={file.errDetail}>
                                                                <InfoCircleOutlined
                                                                    style={{ color: runwayColors.statusGrey }}
                                                                />
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Text>
                                            )}
                                        </List.Item>
                                    ))}
                                </List>
                            }
                        </div>

                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => setShowBumpVersionFilesModal(true)}
                        >
                            Edit
                        </PermissionedActionButton>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item
                    label={
                        <Space>
                            <Text style={{ color: runwayColors.darkTextGrey }}>Localization directories</Text>
                            <TooltipInfoIcon tooltipContent="The directories containing your localization files." />
                        </Space>
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {translationDirs.length === 0 ?
                                <>
                                    <Text disabled style={{ cursor: 'initial' }}>
                                        No localization directories configured.
                                    </Text>
                                </>
                            :   <List
                                    size="small"
                                    split={false}
                                    loadMore={
                                        (
                                            appSettings.translationDirectories.length > truncatedDirsCount &&
                                            !showAllTranslationDirs
                                        ) ?
                                            <Button
                                                type="link"
                                                onClick={() => setShowAllTranslationDirs(true)}
                                                style={{ padding: 0 }}
                                            >
                                                Show more
                                            </Button>
                                        :   null
                                    }
                                >
                                    {translationDirs.map((dir, i) => (
                                        <List.Item
                                            key={i}
                                            style={{
                                                paddingTop: 2,
                                                paddingBottom: 2,
                                                paddingLeft: 0,
                                                paddingRight: 0,
                                            }}
                                        >
                                            {dir.name}
                                        </List.Item>
                                    ))}
                                </List>
                            }
                        </div>

                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => setShowTranslationDirectoriesModal(true)}
                        >
                            Edit
                        </PermissionedActionButton>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Required release steps">
                    <div
                        style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ position: 'absolute', top: 0, right: 0 }}>
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => setShowNonBlockingStepsModal(true)}
                            >
                                Edit
                            </PermissionedActionButton>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                marginTop: 40,
                            }}
                        >
                            <div>
                                {items.map((releaseTypeItem) => {
                                    const type = getReleaseTypeFromReleaseTypeForNonBlockingSteps(releaseTypeItem.key);
                                    const allSteps = releaseStepsByReleaseType[type] ?? [];
                                    return (
                                        <React.Fragment key={releaseTypeItem.key}>
                                            <Space style={{ display: 'block', paddingBottom: '10px' }}>
                                                <Text>{releaseTypeItem.label}</Text>
                                            </Space>
                                            {allSteps.map((step, idx) => {
                                                const nonBlockingStep =
                                                    nonBlockingStepsConfig[releaseTypeItem.key][step] ==
                                                    ReleaseStepBlockingConfig.NonBlocking;
                                                return (
                                                    <React.Fragment key={step}>
                                                        <span
                                                            style={{
                                                                color: `${nonBlockingStep ? antGrey.grey6 : 'initial'}`,
                                                                textDecoration:
                                                                    nonBlockingStep ? 'line-through' : 'none',
                                                            }}
                                                        >
                                                            {displayTextForReleaseStepType(
                                                                step,
                                                                appSettings.platform,
                                                                appStoreType,
                                                                isWorkflowsCIStepEnabled,
                                                            )}
                                                        </span>
                                                        {idx !== allSteps.length - 1 && (
                                                            <span
                                                                style={{
                                                                    color: `${
                                                                        nonBlockingStep ? antGrey.grey6 : 'initial'
                                                                    }`,
                                                                }}
                                                            >
                                                                ,{' '}
                                                            </span>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                            <Divider />
                                        </React.Fragment>
                                    );
                                })}
                                <Text>
                                    Hide steps in the release timeline that are not required:{' '}
                                    {shouldHideNonBlockingSteps ? 'Yes' : 'No'}
                                </Text>
                            </div>
                        </div>
                    </div>
                </Descriptions.Item>
                {getIsApplePlatform(appSettings.platform) && (
                    <Descriptions.Item
                        label={
                            <Space>
                                <Text style={{ color: runwayColors.darkTextGrey }}>
                                    Bundle size device model{hasMultiplePlatforms && ' (iOS only)'}
                                </Text>
                                <TooltipInfoIcon tooltipContent="The selected device model is used when fetching bundle download and install size data from App Store Connect for org overview charts and bundle size alerts." />
                            </Space>
                        }
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text
                                style={
                                    bundleSizeDeviceModelDisplay === 'Invalid' ?
                                        { color: runwayColors.statusRed }
                                    :   undefined
                                }
                            >
                                {bundleSizeDeviceModelDisplay}
                            </Text>
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => setShowEditBundleSizeDeviceModelModal(true)}
                            >
                                Edit
                            </PermissionedActionButton>
                        </div>
                    </Descriptions.Item>
                )}
                {/*<Descriptions.Item*/}
                {/*    label={*/}
                {/*        <Space>*/}
                {/*            <Text>Included file paths</Text>*/}
                {/*            <TooltipInfoIcon tooltipContent="Code changes made outside of these file path(s) will be not be included in feature readiness items." />*/}
                {/*            </Tooltip>*/}
                {/*        </Space>*/}
                {/*    }*/}
                {/*>*/}
                {/*    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>*/}
                {/*        <div style={{ display: 'flex', flexDirection: 'column' }}>*/}
                {/*            {appSettings.filterPaths.length === 0 ? (*/}
                {/*                <>*/}
                {/*                    <Text disabled style={{ cursor: 'initial' }}>*/}
                {/*                        None - Runway will process all code changes that happen in your project&apos;s*/}
                {/*                        root directory.*/}
                {/*                    </Text>*/}
                {/*                </>*/}
                {/*            ) : (*/}
                {/*                appSettings.filterPaths.map((file, index) => (*/}
                {/*                    <Text*/}
                {/*                        key={file}*/}
                {/*                        style={{*/}
                {/*                            marginBottom: index === appSettings.filterPaths.length - 1 ? 0 : 8,*/}
                {/*                        }}*/}
                {/*                        code*/}
                {/*                    >*/}
                {/*                        {file}*/}
                {/*                    </Text>*/}
                {/*                ))*/}
                {/*            )}*/}
                {/*        </div>*/}

                {/*        <PermissionedActionButton*/}
                {/*            actionType={RunwayUserAction.UAUpdateAppSettings}*/}
                {/*            type="link"*/}
                {/*            icon={<EditOutlined />}*/}
                {/*            onClick={() => setShowFilterPathsModal(true)}*/}
                {/*        >*/}
                {/*            Edit*/}
                {/*        </PermissionedActionButton>*/}
                {/*    </div>*/}
                {/*</Descriptions.Item>*/}
            </Descriptions>

            {!hasIntegrations && (
                <Space direction="vertical" size="middle">
                    <Typography>
                        Looks like you don&apos;t have any integrations set up. Click below to get started.
                    </Typography>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            history.push(integrationsRoute);
                        }}
                    >
                        Add integrations
                    </Button>
                </Space>
            )}
            <EditAppSettingsModal
                open={showEditAppSettings}
                onCancel={() => setShowEditAppSettings(false)}
                orgId={props.orgId}
                appId={props.appId}
            />
            <BumpVersionFilesModal
                initialFiles={appSettings.bumpVersionFiles}
                open={showBumpVersionFilesModal}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setShowBumpVersionFilesModal(false)}
            />
            <TranslationDirectoriesModal
                initialDirs={appSettings.translationDirectories}
                open={showTranslationDirectoriesModal}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setShowTranslationDirectoriesModal(false)}
            />
            <NonBlockingReleaseStepsModal
                open={showNonBlockingStepsModal}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setShowNonBlockingStepsModal(false)}
            />
            <FilterPathsModal
                initialPaths={appSettings.filterPaths}
                open={showFilterPathsModal}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setShowFilterPathsModal(false)}
                platform={appSettings.platform}
            />
            {getIsApplePlatform(appSettings.platform) && (
                <EditBundleSizeDeviceModelModal
                    open={showEditBundleSizeDeviceModelModal}
                    appId={props.appId}
                    orgId={props.orgId}
                    onCancel={() => setShowEditBundleSizeDeviceModelModal(false)}
                />
            )}
        </div>
    );
};

export default AppSettingDescription;
